/**
 * 69.29 step 3b (DECISIONS 245 (4), the owner: "yep let's do v for aim"): the tank gunner's sight — V in the turret's seat
 * looks through the gun, a reticle at its centre with the loaded round's drop marks to its range, and the gun's state.
 * DECISIONS 412 / 413 (the owner picked the sheet's row 7, "the leans together", design_sheets/gunner_sight.html): a round
 * sight picture, the rest black; a ranging chevron with the loaded round's small chevrons at each 100 people of its drop, lead
 * ticks every 10 mils to 60 and the same lead as dots on each range's row, the stadia high on the left; magnification steps
 * ×2 / ×4 / ×8 on the wheel, the step lit; a lased range (Space) held in four digits under the picture; the rounds and READY /
 * LOADING as lamps, the reload an arc round the rim. At ×8 a held range lays the gun up for it (the host: the lay folded into
 * the aim it sends) and the marks are drawn from it. Presentation only: the host places the camera, this draws the overlay;
 * the marks' angles are a pure function of the round (its speed, its share of the felt gravity).
 */
import type { TankRound } from '../types/body';

/** The sight's field as a share of the usual view's at today's step (×4). */
export const GUNSIGHT_FOV_SHARE = 0.25;
/** 413: the magnification steps (the field the usual view's over the step), the wheel's; ×4 the default. */
export const GUNSIGHT_STEPS = [2, 4, 8] as const;
export type GunsightStep = typeof GUNSIGHT_STEPS[number];
/** 413 (the owner: "Range-set assist"): the step at which a held range lays the gun up for it. */
export const GUNSIGHT_ASSIST_STEP: GunsightStep = 8;
/** The ranges (people) the reticle marks the loaded round's drop at. */
export const GUNSIGHT_RANGES = [100, 200, 300, 400] as const;
/** The stadia's target: a tank's height (people, its turret top: the hitbox's). */
export const STADIA_HEIGHT = 1.5;

/**
 * Each range's mark below the reticle's centre (pixels): the round's fall over its flight there (no drag — the tank's rounds
 * have none) as an angle under the line, at a field of `fovDeg` degrees (vertical) on a view `height` pixels tall.
 */
export function dropMarks(speed: number, gravity: number, person: number, fovDeg: number, height: number, ranges: readonly number[] = GUNSIGHT_RANGES): Array<{ people: number; px: number }> {
  const half = Math.tan((fovDeg * Math.PI) / 360);
  return dropAngles(speed, gravity, person, ranges).map(m => ({ people: m.people, px: (Math.tan(m.rad) / half) * (height / 2) }));
}

/** 413: each range's drop as an angle under the line (rad). */
export function dropAngles(speed: number, gravity: number, person: number, ranges: readonly number[] = GUNSIGHT_RANGES): Array<{ people: number; rad: number }> {
  return ranges.map(people => {
    const d = people * person, t = d / speed, fall = 0.5 * gravity * t * t;
    return { people, rad: Math.atan(fall / d) };
  });
}

/** 413: the wheel's next step — toward the target (a wheel up, deltaY < 0) in, away out; held at the ends. */
export function nextGunsightStep(step: GunsightStep, deltaY: number): GunsightStep {
  const i = GUNSIGHT_STEPS.indexOf(step), j = Math.max(0, Math.min(GUNSIGHT_STEPS.length - 1, i + (deltaY < 0 ? 1 : -1)));
  return GUNSIGHT_STEPS[j];
}

/** 413: the lased range's four digits (people), "----" with no return, blank before the first lase. */
export function rangeDigits(lased: number | null | undefined): string {
  return lased === undefined ? '' : lased === null ? '----' : String(Math.min(9999, Math.max(0, Math.round(lased)))).padStart(4, '0');
}

/**
 * The gun's state and the sight's. `drops`: the loaded round's (else the next one's) at each range in its reach; `lay` the
 * angle the gun is laid up over the sight's line (413's assist: the marks drawn from it); `lased` the held range (people;
 * null no return; absent not lased).
 */
export interface GunsightState {
  round: TankRound | null; next: TankRound; he: number; solid: number; reloading: boolean; reloadLeft?: number; reload: number;
  fovDeg: number; step: GunsightStep; drops: Array<{ people: number; rad: number }>; lay: number; lased?: number | null;
}

const WORD: Record<TankRound, string> = { he: 'HE', solid: 'SOLID' };
const PALE = 'rgba(216,240,200,0.92)', AMBER = '#ffb340', GREEN = '#7dff8a', DIGIT = '#ff7a3c';

/** The overlay (a canvas over the page, made on first use and kept): drawn with the gun's state, or hidden with null. */
export function drawGunsight(el: HTMLCanvasElement | null, s: GunsightState | null): HTMLCanvasElement | null {
  if (!s) { if (el) el.style.display = 'none'; return el; }
  const cv = el ?? makeSight();
  cv.style.display = 'block';
  const W = window.innerWidth, H = window.innerHeight, dpr = Math.min(window.devicePixelRatio || 1, 2);
  if (cv.width !== Math.round(W * dpr) || cv.height !== Math.round(H * dpr)) { cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr); }
  const ctx = cv.getContext('2d');
  if (!ctx) return cv;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  paintSight(ctx, W, H, s);
  return cv;
}

function makeSight(): HTMLCanvasElement {
  const cv = document.createElement('canvas');
  cv.id = 'tank-gunsight';
  cv.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:41;';
  document.body.appendChild(cv);
  return cv;
}

/** Row 7 of the sheet, in a W × H screen's pixels. */
function paintSight(ctx: CanvasRenderingContext2D, W: number, H: number, s: GunsightState): void {
  const cx = W / 2, cy = H / 2, vmin = Math.min(W, H) / 100, R = 44 * vmin;
  const f = (H / 2) / Math.tan((s.fovDeg * Math.PI) / 360), px = (rad: number) => Math.tan(rad) * f, mil = (m: number) => px(m / 1000);
  const round = s.round ?? s.next, loading = s.reloading;
  const line = (pts: Array<[number, number]>, color = PALE, w = 1.2) => {
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (const [stroke, width] of [['rgba(0,0,0,0.45)', w + 1.6], [color, w]] as const) {
      ctx.strokeStyle = stroke; ctx.lineWidth = width; ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.stroke();
    }
  };
  const text = (t: string, x: number, y: number, o: { size?: number; color?: string; align?: CanvasTextAlign; font?: string; glow?: string } = {}) => {
    ctx.font = o.font ?? `600 ${o.size ?? 11}px system-ui, sans-serif`; ctx.textAlign = o.align ?? 'left'; ctx.textBaseline = 'middle';
    ctx.shadowColor = o.glow ?? 'rgba(0,0,0,0.8)'; ctx.shadowBlur = o.glow ? 8 : 2;
    ctx.fillStyle = o.color ?? PALE; ctx.fillText(t, x, y); ctx.shadowBlur = 0;
  };
  const chev = (x: number, y: number, size: number, color = PALE, w = 1.4) => line([[x - size, y + size * 1.1], [x, y], [x + size, y + size * 1.1]], color, w);

  // the eyepiece: the glass's tint, the picture's edge going dark, the rest black, the eyecup's rim
  ctx.fillStyle = 'rgba(24,40,20,0.07)'; ctx.fillRect(0, 0, W, H);   // (a wash over the world below: the overlay's own canvas has nothing to multiply)
  const g = ctx.createRadialGradient(cx, cy, R * 0.8, cx, cy, R);
  g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(0.75, 'rgba(0,0,0,0.35)'); g.addColorStop(1, 'rgba(3,4,3,1)');
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#030403'; ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.arc(cx, cy, R - 0.5, 0, Math.PI * 2, true); ctx.fill();
  ctx.strokeStyle = 'rgba(70,74,70,0.55)'; ctx.lineWidth = 0.6 * vmin; ctx.beginPath(); ctx.arc(cx, cy, R + 1.1 * vmin, 0, Math.PI * 2); ctx.stroke();

  // the reticle, inside the picture
  ctx.save();
  ctx.beginPath(); ctx.arc(cx, cy, R - 0.4 * vmin, 0, Math.PI * 2); ctx.clip();
  chev(cx, cy, 2.3 * vmin, PALE, 1.6);
  for (const side of [-1, 1]) {   // the lead ticks, every 10 mils to 60 (the long ones 20, 40, 60, their tens under them)
    line([[cx + side * 3.4 * vmin, cy], [cx + side * mil(62), cy]], PALE, 1);
    for (let m = 10; m <= 60; m += 10) {
      const x = cx + side * mil(m), long = m % 20 === 0;
      line([[x, cy], [x, cy + (long ? 1.5 : 0.8) * vmin]], PALE, 1);
      if (long) text(String(m / 10), x, cy + 2.6 * vmin, { size: 10, align: 'center' });
    }
  }
  // the drop scale (from the gun's lay: at ×8 with a range held, the marks from it), the lead as dots on each row
  const marks = s.drops.map(d => ({ people: d.people, y: cy + px(d.rad - s.lay) }));
  for (const m of marks) {
    chev(cx, m.y, 1.1 * vmin);
    text(String(m.people / 100), cx + 1.9 * vmin, m.y + 0.5 * vmin);
    ctx.fillStyle = PALE;
    for (const side of [-1, 1]) for (let l = 10; l <= 60; l += 10) { ctx.beginPath(); ctx.arc(cx + side * mil(l), m.y + 0.6 * vmin, l % 20 === 0 ? 1.4 : 0.9, 0, Math.PI * 2); ctx.fill(); }
  }
  if (marks.length) text(WORD[round], cx, Math.min(...marks.map(m => m.y)) - 2.4 * vmin, { size: 10, align: 'center' });
  // the stadia, high on the left: the base under a tank's tracks, the curve at its turret top, by range (hundreds)
  const x0 = cx - 31 * vmin, x1 = cx - 13 * vmin, yb = cy - 12 * vmin, at = (m: number) => x0 + ((m - 100) / 300) * (x1 - x0);
  line([[x0, yb], [x1, yb]], PALE, 1);
  const curve: Array<[number, number]> = [];
  for (let m = 100; m <= 400; m += 10) curve.push([at(m), yb - px(Math.atan(STADIA_HEIGHT / m))]);
  line(curve, PALE, 1);
  for (const m of GUNSIGHT_RANGES) { line([[at(m), yb], [at(m), yb + 0.8 * vmin]], PALE, 1); text(String(m / 100), at(m), yb + 2.1 * vmin, { size: 10, align: 'center' }); }
  ctx.restore();

  // the step, lit in the frame; the range held, under the picture on the left (SET by it while the gun's laid up for it)
  text(`×${s.step}`, cx + R * 0.72, cy - R * 0.72, { size: 13, align: 'center' });
  const digits = rangeDigits(s.lased);
  if (digits) {
    const x = cx - R * 0.52, y = cy + R * 0.78 + vmin, lit = s.lased !== null;
    ctx.fillStyle = 'rgba(0,0,0,0.55)'; ctx.fillRect(x - 5.4 * vmin, y - 1.9 * vmin, 10.8 * vmin, 3.8 * vmin);
    text(digits, x, y, { font: `700 ${Math.round(2.6 * vmin)}px ui-monospace, Menlo, monospace`, align: 'center', color: lit ? DIGIT : 'rgba(255,122,60,0.55)', glow: lit ? 'rgba(255,90,30,0.8)' : undefined });
    if (s.lay > 0) text('SET', x + 6.4 * vmin, y, { size: 11, color: DIGIT });
  }

  // the rounds and READY / LOADING as lamps, low on the right; a reload an arc round the rim
  const lamps: Array<{ label: string; lit: boolean; color: string }> = [
    { label: `HE ${s.he}`, lit: round === 'he', color: AMBER }, { label: `SOLID ${s.solid}`, lit: round === 'solid', color: AMBER },
    { label: loading ? 'LOADING' : s.round ? 'READY' : 'EMPTY', lit: loading || !!s.round, color: loading ? AMBER : GREEN },
  ];
  let x = cx + R * 0.36, y = cy + R * 0.78;
  for (const l of lamps) {
    ctx.font = '700 11px system-ui, sans-serif';
    const w = ctx.measureText(l.label).width + 12;
    ctx.fillStyle = l.lit ? l.color : 'rgba(0,0,0,0.5)'; ctx.globalAlpha = l.lit ? 0.92 : 1;
    ctx.beginPath(); ctx.roundRect(x, y - 9, w, 18, 3); ctx.fill(); ctx.globalAlpha = 1;
    ctx.strokeStyle = l.lit ? l.color : 'rgba(216,240,200,0.3)'; ctx.lineWidth = 1; ctx.stroke();
    text(l.label, x + w / 2, y, { align: 'center', color: l.lit ? '#141808' : 'rgba(216,240,200,0.5)', font: '700 11px system-ui, sans-serif' });
    x += w + 6;
    if (l.label.startsWith('SOLID')) { x = cx + R * 0.36; y += 23; }
  }
  if (loading && s.reloadLeft !== undefined) {
    const done = Math.max(0, Math.min(1, 1 - s.reloadLeft / s.reload));
    ctx.strokeStyle = AMBER; ctx.lineWidth = 0.6 * vmin; ctx.lineCap = 'butt';
    ctx.beginPath(); ctx.arc(cx, cy, R + 1.1 * vmin, -Math.PI / 2, -Math.PI / 2 + done * Math.PI * 2); ctx.stroke();
  }
}
